import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import RequirePermission from '../components/AuthGuards/RequirePermission';
import { usePermissions } from '../hooks/usePermissions';
import { ORDER_REQUESTS_ENABLED } from '../api/orderRequests/config';
import { ADMIN_REQUESTS_ENABLED } from '../api/adminRequests/config';
import { baseRoutes } from '../constants/routes.constants';

const MyRequestsPage = lazy(() => import('../pages/MyRequests/MyRequestsPage'));
const MyRequestDetailPage = lazy(() => import('../pages/MyRequests/MyRequestDetailPage'));
const AdminRequestPage = lazy(() => import('../pages/Admin/requests/AdminRequestPage'));

/**
 * BUYERS ONLY (order requests, 3 Nov): licence.purchase at 'own', with a company. RequirePermission first (signed in,
 * a role, the key held; a buyer with no company gets the waiting page there). Then the exact level: Platform Admin holds
 * licence.purchase at 'full' and is sent home, as is every other role. A courtesy: the gateway scopes every request to
 * the verified caller.
 */
const BuyerOnly = ({ children }: { children: ReactNode }) => {
  const { getAccessLevel, tenantId } = usePermissions();
  if (getAccessLevel('licence.purchase') !== 'own' || !tenantId) return <Navigate to={baseRoutes.root} replace />;
  return <Suspense fallback={<></>}>{children}</Suspense>;
};

const RequireBuyer = ({ children }: { children: ReactNode }) => (
  <RequirePermission permission="licence.purchase">
    <BuyerOnly>{children}</BuyerOnly>
  </RequirePermission>
);

/** PLATFORM ADMIN ONLY: orders.support.read at exactly 'full' (the Requests tab's own rule, sections.tsx). */
const AdminOnly = ({ children }: { children: ReactNode }) => {
  const { getAccessLevel } = usePermissions();
  if (getAccessLevel('orders.support.read') !== 'full') return <Navigate to={baseRoutes.root} replace />;
  return <Suspense fallback={<></>}>{children}</Suspense>;
};

const RequirePlatformAdmin = ({ children }: { children: ReactNode }) => (
  <RequirePermission permission="orders.support.read">
    <AdminOnly>{children}</AdminOnly>
  </RequirePermission>
);

// Registered only with their flags on: off (UAT and prod today), the addresses do not exist and the site is exactly
// today's. Literal patterns, not the encoded helpers in routes.constants.ts (which would encode the ':').
export const MyRequestsRoutes = ORDER_REQUESTS_ENABLED
  ? [
      { path: '/my-requests', element: <RequireBuyer><MyRequestsPage /></RequireBuyer> },
      { path: '/my-requests/:id', element: <RequireBuyer><MyRequestDetailPage /></RequireBuyer> },
    ]
  : [];

export const AdminRequestRoutes = ADMIN_REQUESTS_ENABLED
  ? [{ path: '/admin/requests/:id', element: <RequirePlatformAdmin><AdminRequestPage /></RequirePlatformAdmin> }]
  : [];
